import "./style.css";
import { parseMode } from "./core/mode";
import { hasStoredSave, readSave, type SaveData } from "./core/save";
import { Game, type GameOptions } from "./game";
import { MainMenu, randomSeed, type WorldSetup } from "./ui/menu";

const container = must(document.getElementById("app"), "app");
const hudRoot = must(document.getElementById("hud"), "hud");
const menuRoot = must(document.getElementById("menu"), "menu");
const fatalRoot = must(document.getElementById("fatal"), "fatal");

const params = new URLSearchParams(location.search);
const urlSeed = params.has("seed") ? Number(params.get("seed")) : Number.NaN;
const urlDistance = params.has("renderDistance") ? Number(params.get("renderDistance")) : Number.NaN;
// A URL that names a world (or asks to play) skips the title screen, so shared
// links and tooling drop straight into the game. A bare visit gets the menu.
const explicitStart = params.has("seed") || params.has("renderDistance") || params.get("play") === "1";
const wantsContinue = params.get("continue") === "1";

type BootRequest =
  | { kind: "new"; setup: WorldSetup }
  | { kind: "saved"; save: SaveData };

let game: Game | null = null;
let starting = false;
/** The boot in flight, reused by the failure panel's retry. */
let pendingBoot: BootRequest | null = null;

const menu = new MainMenu(menuRoot);
menu.preset({
  mode: parseMode(params.get("mode")),
  seed: Number.isFinite(urlSeed) ? Math.trunc(urlSeed) : null,
  renderDistance: Number.isFinite(urlDistance) ? urlDistance : null,
});
menu.onPlay = (setup) => startBoot({ kind: "new", setup });
menu.onContinue = (save) => startBoot({ kind: "saved", save });

/** Boot a fresh world from the menu selection. */
function enterGame(setup: WorldSetup): void {
  startBoot({ kind: "new", setup });
}

function setupFor(request: BootRequest): WorldSetup {
  if (request.kind === "new") return request.setup;
  return {
    seed: request.save.world.seed,
    mode: request.save.world.mode,
    renderDistance: request.save.world.renderDistance,
  };
}

/** Show the loading screen and boot, remembering the request for a retry. */
function startBoot(request: BootRequest): void {
  if (starting || game) return;
  pendingBoot = request;
  starting = true;
  menu.showLoading(setupFor(request), request.kind === "saved");

  // Give the loading screen a frame to paint: the game builds the whole
  // starting area in its constructor before the first frame renders.
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      void bootWorld(request);
    });
  });
}

async function bootWorld(request: BootRequest): Promise<void> {
  try {
    game = await createGame(request);
    game.start();
    pendingBoot = null;
    clearAutoReloadFlag();
    clearResumeSetup();
    hideFatal();
    menu.hide();
    hudRoot.style.display = "block";
    starting = false;
    syncUrl(setupFor(request));
  } catch (err) {
    game = null;
    const support = webglSupport();
    // A reload rebuilds the whole graphics stack and is the surest cure for a
    // refused context — but it cannot conjure WebGL where the browser has none,
    // and a pointless reload would just dump the player back at the menu. When
    // WebGL exists but was refused anyway, reload once and resume this exact
    // start on the fresh page.
    if ((support.webgl2 || support.webgl1) && shouldAutoReload()) {
      saveResumeSetup(request);
      location.reload();
      return;
    }
    menu.setWarning(warningTextFor(support));
    showFatal(err, support);
  }
}

const RESUME_KEY = "voxelcraft:resume-setup";

/**
 * Remember the start that was interrupted (by an automatic reload) so the
 * next page load resumes it instead of dropping the player on the menu. Saved
 * games only need a marker; their state is already in the save slot.
 */
function saveResumeSetup(request: BootRequest): void {
  try {
    const payload =
      request.kind === "saved" ? { kind: "saved" } : { kind: "new", setup: request.setup };
    sessionStorage.setItem(RESUME_KEY, JSON.stringify(payload));
  } catch {
    // Private mode and friends: the URL autostart still covers seeded visits.
  }
}

/** The interrupted start queued by a previous automatic reload, if any. */
function readResumeSetup(): BootRequest | null {
  try {
    const raw = sessionStorage.getItem(RESUME_KEY);
    if (raw === null) return null;
    const parsed = JSON.parse(raw) as { kind?: unknown; setup?: unknown };
    if (parsed.kind === "saved") {
      const save = readSave();
      return save ? { kind: "saved", save } : null;
    }
    const setup = parseSetup(parsed.kind === "new" ? parsed.setup : parsed);
    return setup ? { kind: "new", setup } : null;
  } catch {
    return null;
  }
}

/** Accept both the current resume payload and the older bare setup shape. */
function parseSetup(value: unknown): WorldSetup | null {
  if (typeof value !== "object" || value === null) return null;
  const parsed = value as Partial<WorldSetup>;
  if (typeof parsed.seed !== "number" || !Number.isFinite(parsed.seed)) return null;
  return {
    seed: Math.trunc(parsed.seed),
    mode: parseMode(typeof parsed.mode === "string" ? parsed.mode : null),
    renderDistance: Number.isFinite(parsed.renderDistance)
      ? Number(parsed.renderDistance)
      : menu.renderDistance,
  };
}

function clearResumeSetup(): void {
  try {
    sessionStorage.removeItem(RESUME_KEY);
  } catch {
    // Nothing stored in the first place.
  }
}

const AUTO_RELOAD_KEY = "voxelcraft:auto-reloaded-at";
/** Suppress a second automatic reload this soon after the previous one. */
const AUTO_RELOAD_COOLDOWN_MS = 30_000;

/**
 * True when a failed boot should be retried with a page reload. The cooldown
 * keeps a machine that cannot create WebGL contexts at all from reload-looping:
 * the second failure in a row shows the failure panel instead.
 */
function shouldAutoReload(): boolean {
  try {
    const last = Number(sessionStorage.getItem(AUTO_RELOAD_KEY) ?? 0);
    if (Number.isFinite(last) && last > 0 && Date.now() - last < AUTO_RELOAD_COOLDOWN_MS) {
      return false;
    }
    sessionStorage.setItem(AUTO_RELOAD_KEY, String(Date.now()));
    return true;
  } catch {
    // No sessionStorage access: leave reloading to the player.
    return false;
  }
}

/** A successful boot re-arms the automatic-reload recovery. */
function clearAutoReloadFlag(): void {
  try {
    sessionStorage.removeItem(AUTO_RELOAD_KEY);
  } catch {
    // Private mode and friends: nothing to clear.
  }
}

/**
 * Delays between boot attempts, spent while the browser refuses a WebGL
 * context. Refusals are usually transient — the GPU process may be respawning
 * after a crash, contexts from earlier page loads may not have been released
 * yet, or memory may be tight — so the attempts stretch over a dozen seconds
 * and the loading screen reports the wait instead of giving up early.
 */
const BOOT_DELAYS_MS = [0, 300, 700, 1500, 2500, 3500, 4000];

/**
 * Build a game, retrying while the failure looks like a refused WebGL context,
 * so the common transient refusals heal without the player reloading.
 */
async function createGame(request: BootRequest): Promise<Game> {
  let lastError: unknown = null;
  for (let attempt = 0; attempt < BOOT_DELAYS_MS.length; attempt++) {
    const delay = BOOT_DELAYS_MS[attempt];
    if (delay > 0) {
      menu.setLoadingNote(
        `Waiting for the graphics driver… (attempt ${attempt + 1} of ${BOOT_DELAYS_MS.length})`,
      );
      await sleep(delay);
    }
    try {
      const options: GameOptions =
        request.kind === "saved"
          ? {
              savedGame: request.save,
              onExit: leaveGame,
              onOptions: () => menu.openSettingsFromGame(() => {}),
            }
          : {
              seed: request.setup.seed,
              mode: request.setup.mode,
              renderDistance: request.setup.renderDistance,
              onExit: leaveGame,
              onOptions: () => menu.openSettingsFromGame(() => {}),
            };
      return new Game(container, hudRoot, options);
    } catch (err) {
      lastError = err;
      if (!isContextError(err)) throw err;
    }
  }
  throw lastError;
}

/** Matches the browser's "Error creating WebGL context." and similar refusals. */
function isContextError(err: unknown): boolean {
  return err instanceof Error && /webgl|context/i.test(err.message);
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/** Tear the world down and go back to the title screen. */
function leaveGame(): void {
  game?.dispose();
  game = null;
  clearResumeSetup();
  refreshWebglWarning();
  menu.show();
  hudRoot.style.display = "none";
  clearWorldUrl();
}

/** Drop the active-world parameters when returning to the title screen. */
function clearWorldUrl(): void {
  const url = new URL(location.href);
  for (const key of ["seed", "mode", "renderDistance", "continue"]) {
    url.searchParams.delete(key);
  }
  history.replaceState(null, "", url);
}

/**
 * Keep the world shareable and reload-safe. The seed/mode still describe a new
 * world for recipients without this save, while `continue=1` tells this browser
 * to resume its local save instead of generating over it.
 */
function syncUrl(setup: WorldSetup): void {
  const url = new URL(location.href);
  url.searchParams.set("seed", String(setup.seed));
  url.searchParams.set("mode", setup.mode);
  url.searchParams.set("renderDistance", String(setup.renderDistance));
  url.searchParams.set("continue", "1");
  history.replaceState(null, "", url);
}

interface WebglSupport {
  webgl2: boolean;
  webgl1: boolean;
}

/**
 * What the browser will still hand out right now. Used to explain a refused
 * boot with something more useful than "it failed": "no WebGL at all" points
 * at hardware acceleration settings, "only WebGL1" at a restart.
 */
function webglSupport(): WebglSupport {
  const probe = (type: "webgl2" | "webgl"): boolean => {
    try {
      const canvas = document.createElement("canvas");
      const gl = canvas.getContext(type) as WebGL2RenderingContext | WebGLRenderingContext | null;
      if (!gl) return false;
      gl.getExtension("WEBGL_lose_context")?.loseContext();
      return true;
    } catch {
      return false;
    }
  };
  return { webgl2: probe("webgl2"), webgl1: probe("webgl") };
}

/** Plain-language next steps for a refused context, based on a live probe. */
function webglAdvice(support: WebglSupport): string {
  const { webgl2, webgl1 } = support;
  const seen = `Diagnostics: WebGL2 ${webgl2 ? "works" : "unavailable"}, WebGL1 ${
    webgl1 ? "works" : "unavailable"
  }.`;
  if (webgl2) {
    return `${seen} WebGL2 works right now, so the refusal was transient — Try Again should get you in.`;
  }
  if (webgl1) {
    return `${seen} Only WebGL1 is being served, and this build needs WebGL2. Restarting Chrome usually restores WebGL2 once the GPU process has recovered.`;
  }
  return `${seen} The browser is currently providing no WebGL at all. Usual causes, in order: hardware acceleration is off (turn on "Use graphics acceleration when available" in chrome://settings/system, then restart Chrome); the GPU process crashed or the GPU is blocklisted (check chrome://gpu); or Chrome's software WebGL fallback is disabled (set "Unsafe SwiftShader" at chrome://flags/#enable-unsafe-swiftshader and restart Chrome).`;
}

/** The title-screen warning matching a support state, or null when fine. */
function warningTextFor(support: WebglSupport): string | null {
  if (support.webgl2) return null;
  if (support.webgl1) {
    return "This browser is only providing WebGL1 right now and VoxelCraft needs WebGL2 — restart the browser and try again. If it stays this way, check chrome://gpu.";
  }
  return 'This browser is currently providing no WebGL, so no world can start. To fix it: turn on "Use graphics acceleration when available" in chrome://settings/system and restart the browser; check chrome://gpu for a crashed or blocklisted GPU; or enable "Unsafe SwiftShader" at chrome://flags/#enable-unsafe-swiftshader and restart the browser.';
}

/** Refresh the title-screen WebGL warning from a live probe. */
function refreshWebglWarning(): void {
  menu.setWarning(warningTextFor(webglSupport()));
}

function showFatal(err: unknown, support: WebglSupport): void {
  starting = false;
  menu.hide();
  // Leave the HUD's own widgets untouched: the next world start rebuilds them,
  // and clobbering #hud here would make that start fail as well.
  hudRoot.style.display = "none";
  fatalRoot.hidden = false;
  fatalRoot.innerHTML = `<div class="fatal"><h1>Could not start the game</h1><pre>${
    err instanceof Error ? err.stack ?? err.message : String(err)
  }</pre><p>This build needs WebGL2 and the browser refused a graphics context after several attempts. ${webglAdvice(
    support,
  )}</p><div class="fatal-actions"><button id="fatal-reload" type="button" class="btn primary">Reload Page</button>${
    pendingBoot ? '<button id="fatal-retry" type="button" class="btn">Try Again</button>' : ""
  }<button id="fatal-back" type="button" class="btn">Back to Main Menu</button></div></div>`;
  const reload = fatalRoot.querySelector<HTMLButtonElement>("#fatal-reload");
  if (reload) {
    reload.onclick = () => location.reload();
  }
  const retry = fatalRoot.querySelector<HTMLButtonElement>("#fatal-retry");
  if (retry) {
    retry.onclick = () => {
      if (!pendingBoot) return;
      const request = pendingBoot;
      hideFatal();
      startBoot(request);
    };
  }
  const back = fatalRoot.querySelector<HTMLButtonElement>("#fatal-back");
  if (back) {
    back.onclick = () => {
      hideFatal();
      // The player chose the menu: nothing should resume the interrupted start
      // or auto-create a new world when this page is next reloaded.
      pendingBoot = null;
      clearResumeSetup();
      clearWorldUrl();
      refreshWebglWarning();
      menu.show();
    };
  }
}

function hideFatal(): void {
  fatalRoot.hidden = true;
  fatalRoot.innerHTML = "";
}

// Reloading or navigating away must not strand this page's WebGL context.
// Browsers cap live contexts, and a reload (or a dev-server auto-reload) right
// before starting a world is exactly when the next context request gets
// refused. Releasing the renderer here frees the GPU slot immediately instead
// of waiting for GC.
window.addEventListener("pagehide", (event) => {
  if (event.persisted) return; // The page will be restored: keep the context.
  try {
    game?.dispose();
  } catch {
    // The unload must proceed even if teardown throws.
  }
  game = null;
});

// Dev-server hygiene: when a hot update replaces this module, release the old
// context before a replacement boots a new world.
import.meta.hot?.dispose(() => {
  try {
    game?.dispose();
  } catch {
    // The module is being replaced anyway.
  }
  game = null;
});

// An interrupted automatic start (from a reload that released the graphics
// stack) resumes right here — the player never lands back on the menu.
const resume = readResumeSetup();
if (resume) {
  startBoot(resume);
} else {
  const saved = wantsContinue ? readSave() : null;
  const savedMatchesUrl =
    saved !== null && (!Number.isFinite(urlSeed) || saved.world.seed === Math.trunc(urlSeed));
  if (saved && savedMatchesUrl) {
    startBoot({ kind: "saved", save: saved });
  } else if (wantsContinue && hasStoredSave()) {
    // The URL asked to continue but the slot is unreadable or belongs to a
    // different world. Show the menu instead of silently replacing the save.
    refreshWebglWarning();
  } else if (explicitStart) {
    enterGame({
      seed: Number.isFinite(urlSeed) ? Math.trunc(urlSeed) : randomSeed(),
      mode: parseMode(params.get("mode")),
      renderDistance: Number.isFinite(urlDistance) ? urlDistance : menu.renderDistance,
    });
  } else {
    // A bare visit gets the title screen; if the browser has no WebGL to give,
    // say so there instead of after a failed start.
    refreshWebglWarning();
  }
}

function must<T extends HTMLElement>(el: T | null, id: string): T {
  if (!el) throw new Error(`index.html is missing #${id}`);
  return el;
}
